import { useMemo, useState } from "react";
import { createFileRoute, useNavigate, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import { Breadcrumbs, PageHeader, PageSkeleton } from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  memberPickerCapped,
  MEMBER_PICKER_PARAMS,
  useCompanyMembers,
} from "@/hooks/use-company-members";
import {
  companyKitRights,
  kitHolderOptions,
  useCompanyTestKit,
  useUpdateTestKit,
  type TestKitUpdate,
} from "@/hooks/use-company-test-kits";
import { KitForm } from "@/components/kits/kit-form";

export const Route = createFileRoute("/_company/company/kits/$kitId/edit")({
  component: EditKitPage,
});

function EditKitPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const toasts = useToast();
  const { kitId } = Route.useParams();
  const contextQuery = useMyCompanyContext();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const selfProfileId = contextQuery.data?.profile.id ?? "";

  const kitQuery = useCompanyTestKit(companyId, kitId);
  const updateMutation = useUpdateTestKit(companyId, kitId);
  const membersQuery = useCompanyMembers(companyId, MEMBER_PICKER_PARAMS);
  const holders = useMemo(
    () => kitHolderOptions(membersQuery.data?.items ?? []),
    [membersQuery.data?.items],
  );
  const holdersCapped = memberPickerCapped(membersQuery.data?.items);
  const [serialConflict, setSerialConflict] = useState(false);
  const [holderNotMember, setHolderNotMember] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // The edit authority is PER ROW — an admin edits every gauge the company
  // owns, a member the ones they hold — so the guard needs the kit itself and
  // cannot live in `beforeLoad`, which runs outside React with neither the
  // membership nor the row. Never redirect on unresolved data: a holder
  // deep-linking cold would be bounced off their own form.
  if (contextQuery.isLoading || kitQuery.isLoading) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <PageSkeleton />
      </div>
    );
  }

  const kit = kitQuery.data;
  if (kitQuery.isError || !kit) {
    return (
      <div className="mx-auto max-w-3xl p-8">
        <Breadcrumbs items={[{ label: t("company:kits.title"), to: "/company/kits" }]} />
        <PageHeader title={t("company:kits.editKit")} />
      </div>
    );
  }

  const rights = companyKitRights(kit, { isAdmin, profileId: selfProfileId });
  if (!rights.canEdit) {
    // Back to the row, which every member may read — not to the list, which
    // would hide what they were looking at.
    return <Navigate to="/company/kits/$kitId" params={{ kitId }} replace />;
  }

  const handleSubmit = (body: TestKitUpdate) => {
    setSerialConflict(false);
    setHolderNotMember(false);
    setRefusal(null);
    updateMutation.mutate(body, {
      onSuccess: () => {
        toasts.success("company:kits.saveSuccess");
        void navigate({ to: "/company/kits/$kitId", params: { kitId } });
      },
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : undefined;
        if (code === "kit_serial_conflict_for_owner") {
          setSerialConflict(true);
          return;
        }
        if (code === "holder_not_member") {
          setHolderNotMember(true);
          return;
        }
        // `not_row_owner` is a verdict about THIS gauge — the holder changed
        // under the reader, or the row was never theirs. It reads as a refusal
        // beside the unsaved form, which still holds their edit.
        if (code === "not_row_owner") {
          setRefusal(t("company:kits.notRowOwner"));
          return;
        }
        toasts.apiError(code);
      },
    });
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <Breadcrumbs
        items={[
          { label: t("company:kits.title"), to: "/company/kits" },
          { label: kit.serial_number },
          { label: t("company:kits.editKit") },
        ]}
      />
      <PageHeader title={t("company:kits.editKit")} />
      <KitForm
        mode="edit"
        initial={kit}
        isSubmitting={updateMutation.isPending}
        serialConflict={serialConflict}
        canChangeHolder={rights.canChangeHolder}
        holders={holders}
        holdersCapped={holdersCapped}
        holderNotMember={holderNotMember}
        refusal={refusal ?? undefined}
        onSubmit={handleSubmit}
        onCancel={() => void navigate({ to: "/company/kits/$kitId", params: { kitId } })}
      />
    </div>
  );
}
